import React, { createRef, useEffect, forwardRef, useState, useCallback, useContext } from 'react';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import styled from 'styled-components';

import {
  ClickableGroup,
  DraggableContainer,
  NodeRect,
  wrappedText,
} from 'src/presentation/components/pages/ivr/components/ivrTree/ivrTreeLayout';
import { getTimeRangeForTemporarySplitNode } from 'src/presentation/components/pages/ivr/components/helpers/temporarySplitNodeHelpers';
import { isTemporarySplitNode } from 'src/presentation/components/pages/ivr/helpers/ivrHelpers';
import { IvrEditorContext } from 'src/presentation/components/pages/ivr/IvrEditor';
import IvrTreeContext from 'src/presentation/components/pages/ivr/components/ivrTree/ivrTreeContext';
import NODE_TYPES from 'src/models/Ivrs/NodeTypes';
import NODES_MAPPER from 'src/presentation/components/pages/ivr/constants/nodesMapper';
import { Point } from 'src/presentation/components/pages/ivr/components/ivrTree/ivrTypes';
import useDragNode from 'src/presentation/components/pages/ivr/components/ivrTree/hooks/useDragNode';

const IvrNodeTextLeftMargin = 15;
const IvrNodeTextTopMargin = 10;
const IvrNodeLabelTopMargin = 26;

const NodeTypeTextMargin = { left: 5, top: 10 };

type IvrNodeBlockProps = {
  'data-aid': string;
  data: any;
  filter?: string;
  height: number;
  highlighted?: boolean;
  selected: boolean;
  transform: string;
  width: number;
  x: number;
  y: number;
};

const IvrNodeBlock = ({ 'data-aid': dataAid, data, ...otherProps }: IvrNodeBlockProps) => {
  const { description, id, name: nodeName, selection, type: nodeType } = data;

  const name = nodeType === NODE_TYPES.IVR_NODE_DIAL ? (isEmpty(selection) ? description : selection) : nodeName;

  let type = nodeType;
  const { invalidFormList } = useContext(IvrEditorContext);
  const { nodeClick } = useContext(IvrTreeContext);

  const click = useCallback(() => {
    nodeClick && nodeClick(data);
  }, [data, nodeClick]);
  let customElements: Function | undefined;
  const nodeId = get(data, 'id', '');
  const nodeIsInvalid = invalidFormList.includes(nodeId);

  if (isTemporarySplitNode(nodeId)) {
    type = NODE_TYPES.IVR_NODE_SCHEDULE_TEMPORARY_SPLIT;

    customElements = () => {
      const p: Point = [118, 8];
      const { isActive } = getTimeRangeForTemporarySplitNode(get(data, 'ivrNode.nodeSchedules', []));
      return (
        <g transform={`translate(${p[0]},${p[1]})`}>
          <TemporarySplitRect isActive={isActive} />
          <TemporarySplitText data-aid={`${dataAid}-status`} x={12.5} y={10.5} textAnchor="middle" isActive={isActive}>
            {isActive ? 'On' : 'Off'}
          </TemporarySplitText>
        </g>
      );
    };
  }

  return (
    <DefaultNode
      customElements={customElements}
      filter="url(#dropshadow)"
      id={id}
      label={name}
      nodeIsInvalid={nodeIsInvalid}
      onClick={click}
      type={type}
      {...otherProps}
    />
  );
};

const TemporarySplitRect = styled.rect`
  width: 25px;
  height: 16px;
  rx: 8px;
  fill: ${p => (p.isActive ? p.theme.colors.green300 : p.theme.colors.white)};
  stroke: ${p => (p.isActive ? p.theme.colors.green300 : p.theme.colors.gray300)};
`;

const TemporarySplitText = styled.text`
  font-size: 7px;
  font-weight: bold;
  fill: ${p => (p.isActive ? p.theme.colors.white : p.theme.colors.black)};
`;

type DefaultNodeProps = {
  customElements?: Function;
  filter?: string;
  height: number;
  highlighted?: boolean;
  id: string;
  label: string[];
  nodeIsInvalid: boolean;
  onClick: Function;
  selected: boolean;
  transform: string;
  type: string;
  width: number;
  x: number;
  y: number;
};

const DefaultNode = forwardRef(
  (
    {
      customElements,
      filter,
      height,
      highlighted,
      id,
      label,
      nodeIsInvalid,
      onClick,
      selected,
      transform,
      type,
      width,
      x,
      y,
    }: DefaultNodeProps,
    ref: any
  ) => {
    const nodeTypeRef = createRef();
    const dataAid = `ivrTreeNode-${id}`;

    const clickHandler = () => {
      onClick && onClick();
    };

    const { setNodeRef } = useDragNode({ nodeId: id, x, y, onClick: clickHandler });

    return (
      <DraggableContainer
        selected={selected}
        highlighted={highlighted}
        nodeIsInvalid={nodeIsInvalid}
        className={`node${selected ? ' selected' : ''}`}
        data-aid={dataAid}
        ref={setNodeRef}
        transform={`translate(${x},${y})`}
      >
        <ClickableGroup className="node-group" transform={transform}>
          <NodeRect
            className="nodeRect"
            filter={filter}
            height={height}
            highlighted={highlighted}
            nodeIsInvalid={nodeIsInvalid}
            ref={ref}
            selected={selected}
            width={width}
            x={0}
            y={0}
          />
          {customElements && customElements()}
          <NodeType
            data-aid={`${dataAid}-type`}
            x={IvrNodeTextLeftMargin}
            y={IvrNodeTextTopMargin}
            text={type}
            ref={nodeTypeRef}
          />
          <NodeLabelText data-aid={`${dataAid}-type-body`} x={IvrNodeTextLeftMargin} y={IvrNodeLabelTopMargin + 13}>
            {wrappedText({
              dataAid: `${dataAid}-type-body`,
              x: IvrNodeTextLeftMargin,
              text: label,
              maxLinesCount: 2,
            })}
          </NodeLabelText>
        </ClickableGroup>
      </DraggableContainer>
    );
  }
);

const NodeType = forwardRef(
  ({ 'data-aid': dataAid, x, y, text }: { 'data-aid': string; x: number; y: number; text: string }, ref: any) => {
    const [nodeTypeBox, setNodeTypeBox] = useState({ width: 0, height: 0 });
    useEffect(() => {
      if (get(ref, 'current')) {
        const { width, height } = ref.current.getBBox();
        setNodeTypeBox({ width, height });
      }
    }, [ref]);

    const { treeNodeLabel, color, icon: Svg } = NODES_MAPPER[text];

    return (
      <g>
        {!!Svg && <Svg width="12px" height="12px" x={x} y={y} fill={color} />}
        <NodeTypeText
          data-aid={`${dataAid}-text`}
          x={x + (nodeTypeBox.width + 20) / 2 + NodeTypeTextMargin.left}
          y={y + NodeTypeTextMargin.top}
          ref={ref}
          textAnchor="middle"
          style={{ fill: color }}
        >
          {treeNodeLabel}
        </NodeTypeText>
      </g>
    );
  }
);

const NodeTypeText = styled.text`
  fill: white;
  font-size: 10px;
`;

const NodeLabelText = styled.text`
  fill: black;
  font-style: normal;
  font-weight: bold;
  font-size: 10px;
  line-height: 162%;
`;

export { IvrNodeBlock };
